// src/pages/PlaylistDetailPage.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, arrayRemove, deleteDoc } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useMusic } from '../context/MusicContext';
import { usePlaylistArtwork } from '../hooks/usePlaylistArtwork';
import { getDisplayArtistNames } from "../Utils/song";
import EditPlaylistModal from '../components/music/EditPlaylistModal';
import { motion, AnimatePresence } from 'framer-motion';
import { useProfile } from '../context/ProfileContext';
import dropdownSidewaysIcon from '../assets/dropdownsideways_icon.png';
import addToPlaylistIcon from '../assets/addtoplaylist.png';
import ProfileLink from '../components/profile/ProfileLink';
import AddSongToPlaylistModal from '../components/music/AddSongToPlaylistModal';
import toast from 'react-hot-toast';

import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragHandleDots2Icon } from '@radix-ui/react-icons';

const SortableSongRow = ({ 
  song, 
  index, 
  isPlaying, 
  onPlay, 
  isOwner, 
  activeSongMenu, 
  setActiveSongMenu,
  menuPosition,
  setMenuPosition,
  songMenuRef,
  handleGoToArtist,
  handleRemoveSongFromPlaylist,
  isDarkMode // 🌟 Added downstream forward tracking hook
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: song.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 'auto',
    opacity: isDragging ? 0.6 : 1,
  };

  const profileId = song.artistId;

  return (
    <li 
      ref={setNodeRef} 
      style={style}
      // 🌟 UPGRADED: Dynamic lightweight list item hover targets matching Atomic styling parameters
      className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
        isDarkMode 
          ? "hover:bg-zinc-900 text-white" 
          : "hover:bg-gray-100 text-slate-900"
      }`}
    >
      <div 
        className="flex items-center flex-grow cursor-pointer min-w-0"
        onClick={onPlay}
      >
        <img src={song.albumArtworkURL} alt={song.title} className="w-14 h-14 rounded-xl object-cover mr-4 shadow-md flex-shrink-0" />
        <div className="flex-grow min-w-0 pr-2">
          <p className={`font-bold text-base tracking-tight truncate ${isPlaying ? 'text-brand-pink font-black italic' : ''}`}>
            {song.title}
          </p>
          <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
            {song.artistName}
          </p>
        </div>
      </div>

      {isOwner && (
        <div className="relative ml-auto flex-shrink-0" ref={activeSongMenu === song.id ? songMenuRef : null}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const rect = e.currentTarget.getBoundingClientRect();
              if (rect.top > window.innerHeight * 0.60) {
                setMenuPosition('top');
              } else {
                setMenuPosition('bottom');
              }
              setActiveSongMenu(activeSongMenu === song.id ? null : song.id);
            }}
            className={`p-2 rounded-full transition-colors focus:outline-none ${
              isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-200'
            }`}
          >
            <img 
              src={dropdownSidewaysIcon} 
              alt="Song Options" 
              className={`w-6 h-6 ${isDarkMode ? 'invert opacity-70' : ''}`} 
            />
          </button>
          
          <AnimatePresence>
            {activeSongMenu === song.id && (
              // 🌟 UPGRADED: Floating sub-menu cards now hold stark high-contrast baseline layout properties
              <motion.div
                className={`absolute right-0 w-48 border rounded-xl shadow-2xl py-1.5 z-30 ${
                  menuPosition === 'top' ? 'bottom-full mb-2' : 'mt-2'
                } ${
                  isDarkMode ? 'bg-zinc-950 border-zinc-800 text-zinc-300' : 'bg-white border-slate-100 text-slate-700'
                }`}
                initial={{ opacity: 0, scale: 0.95, y: menuPosition === 'top' ? 6 : -6 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: menuPosition === 'top' ? 6 : -6 }}
                transition={{ duration: 0.15 }}
              >
                {profileId && (
                  <button
                    onClick={() => handleGoToArtist(profileId)}
                    className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                      isDarkMode ? 'hover:bg-zinc-900 hover:text-white' : 'hover:bg-gray-50 hover:text-slate-900'
                    }`}
                  >
                    Go to Artist
                  </button>
                )}
                <button
                  onClick={() => handleRemoveSongFromPlaylist(song.id)}
                  className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    isDarkMode ? 'hover:bg-zinc-900 hover:text-red-400' : 'hover:bg-gray-50 hover:text-red-600'
                  }`}
                >
                  Remove from playlist
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      
      {isOwner && (
        <div 
          {...attributes} 
          {...listeners} 
          className={`p-2 cursor-grab active:cursor-grabbing transition-colors touch-none flex-shrink-0 ${
            isDarkMode ? 'text-zinc-600 hover:text-zinc-400' : 'text-gray-400 hover:text-gray-600'
          }`}
        >
          <DragHandleDots2Icon />
        </div>
      )}
    </li>
  );
};

const PlaylistDetailPage = () => {
  const db = getFirestore();
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const [playlist, setPlaylist] = useState(null);
  const [ownerProfile, setOwnerProfile] = useState(null);
  const [songs, setSongs] = useState([]);
  const [initialSongs, setInitialSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddSongModalOpen, setIsAddSongModalOpen] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  const [activeSongMenu, setActiveSongMenu] = useState(null);
  const songMenuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState('bottom');

  const { setQueueAndPlay, currentSong } = useMusic();
  const { activeProfile, user } = useProfile();
  const { isDarkMode } = useProfile(); // 🌟 IMPORT AND MAP THE DYNAMIC PLATFORM COLOR LAYER
  const { artworkUrls, isLoading: isArtworkLoading } = usePlaylistArtwork(playlist);

  

  const fetchPlaylistAndSongs = useCallback(async () => {
    if (!playlistId) return;

    setIsLoading(true);
    try {
      const playlistRef = doc(db, 'playlists', playlistId);
      const playlistSnap = await getDoc(playlistRef);

      if (!playlistSnap.exists()) {
        setPlaylist(null);
        setSongs([]);
        return;
      }
      
      const playlistData = { id: playlistSnap.id, ...playlistSnap.data() };

      const isViewerOwner = activeProfile?.id && playlistData.ownerId === activeProfile.id;

      if (playlistData.isPrivate && !isViewerOwner) {
        setIsUnauthorized(true); // 🔒 Flag it here and exit the fetch cleanly
        setIsLoading(false);
        return;
      }

      setPlaylist(playlistData);

      const ownerPromise = getDoc(doc(db, 'users', playlistData.ownerId));
      const songPromises = (playlistData.songIds || []).map(id => getDoc(doc(db, 'songs', id)));
      
      const [ownerSnap, songDocs] = await Promise.all([ownerPromise, Promise.all(songPromises)]);

      if (ownerSnap.exists()) {
        setOwnerProfile({ id: ownerSnap.id, ...ownerSnap.data() });
      }

      const fetchedSongs = songDocs
        .map(docSnap => docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null)
        .filter(song => song && song.isDeleted !== true);
      
      const orderedSongs = (playlistData.songIds || []).map(id => 
        fetchedSongs.find(s => s.id === id)
      ).filter(Boolean);

      setSongs(orderedSongs);
      setInitialSongs(orderedSongs);

    } catch (error) {
      console.error("Error fetching playlist data:", error);
      setPlaylist(null);
      setSongs([]);
    } finally {
      setIsLoading(false);
    }
  }, [playlistId, db]);

  const handleTogglePrivacy = async () => {
    if (!playlistId || !playlist) return;
    setIsMenuOpen(false); // Close dropdown immediately

    // Determine target state inversion
    const newPrivacyState = !playlist.isPrivate;
    const loadingToast = toast.loading(
      newPrivacyState ? "Setting playlist to private..." : "Making playlist public..."
    );

    try {
      const playlistRef = doc(db, 'playlists', playlistId);
      
      // Update local state optimistically for snappy UI response
      setPlaylist(prev => ({ ...prev, isPrivate: newPrivacyState }));

      // Commit change atomically to Firestore
      await updateDoc(playlistRef, {
        isPrivate: newPrivacyState
      });

      toast.success(
        newPrivacyState ? "Playlist is now private" : "Playlist is now public", 
        { id: loadingToast }
      );
    } catch (error) {
      console.error("Error updating playlist privacy state:", error);
      // Rollback local state if network fails
      setPlaylist(prev => ({ ...prev, isPrivate: !newPrivacyState }));
      toast.error("Failed to update visibility status.", { id: loadingToast });
    }
  };

  const saveOrder = useCallback(async () => {
    const initialOrder = initialSongs.map(s => s.id);
    const currentOrder = songs.map(s => s.id);
    if (JSON.stringify(initialOrder) === JSON.stringify(currentOrder)) {
      return;
    }

    try {
      const playlistRef = doc(db, 'playlists', playlistId);
      await updateDoc(playlistRef, {
        songIds: currentOrder
      });
      setInitialSongs(songs);
      toast.success("Playlist order saved!");
    } catch (error) {
      console.error("Error saving playlist order: ", error);
      toast.error("Failed to save playlist order.");
    }
  }, [playlistId, db, songs, initialSongs]);

  const saveOrderRef = useRef(saveOrder);
  useEffect(() => {
    saveOrderRef.current = saveOrder;
  }, [saveOrder]);

  useEffect(() => {
    return () => {
      saveOrderRef.current();
    };
  }, []);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setSongs((items) => {
        const oldIndex = items.findIndex(s => s.id === active.id);
        const newIndex = items.findIndex(s => s.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  useEffect(() => {
    if (isUnauthorized) {
      toast.error("This playlist is private.", { id: "private-playlist-guard" }); // Use an explicit ID to deduplicate toast frames
      navigate('/charts', { replace: true });
    }
  }, [isUnauthorized, navigate]);

  useEffect(() => {
    fetchPlaylistAndSongs();
  }, [fetchPlaylistAndSongs]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (songMenuRef.current && !songMenuRef.current.contains(event.target)) {
        setActiveSongMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleEditDetails = () => {
    setIsMenuOpen(false);
    setIsEditModalOpen(true);
  };
  
  const handleEditArtwork = () => {
    setIsMenuOpen(false);
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    fileInput.onchange = async () => {
      const file = fileInput.files[0];
      if (!file || !playlistId || !user) return;
      
      const toastId = toast.loading('Uploading artwork...');
      
      try {
        const token = await user.getIdToken();
        const generateUrlResponse = await fetch("https://generateplaylistartworkuploadurl-upfvnj5ioq-uc.a.run.app/generatePlaylistArtworkUploadUrl", {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
                playlistId: playlistId,
                fileName: file.name,
                contentType: file.type,
            }),
        });

        if (!generateUrlResponse.ok) throw new Error('Could not get upload permission.');
        
        const { uploadUrl, finalPath } = await generateUrlResponse.json();

        await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
        });

        const storage = getStorage();
        const fileURL = await getDownloadURL(ref(storage, finalPath));
        
        await updateDoc(doc(db, 'playlists', playlistId), { customArtworkUrl: fileURL });
        setPlaylist(prev => ({ ...prev, customArtworkUrl: fileURL }));
        toast.success("Artwork updated!", { id: toastId });

      } catch (error) {
        toast.error(`Upload failed: ${error.message}`, { id: toastId });
      }
    };
    fileInput.click();
  };

  const handleDeletePlaylist = async () => {
    setIsMenuOpen(false);
    if (window.confirm(`Are you sure you want to delete the playlist "${playlist.name}"? This cannot be undone.`)) {
      try {
        const db = getFirestore();
        await deleteDoc(doc(db, 'playlists', playlistId));
        navigate('/profile?tab=playlists');
      } catch (error) {
        console.error("Error deleting playlist:", error);
        alert("Failed to delete playlist.");
      }
    }
  };

  const handlePlaySong = (songIndex) => {
    setQueueAndPlay(songs, songIndex, playlist.name);
  };

  const handleRemoveSongFromPlaylist = async (songIdToRemove) => {
    if (!playlistId || !songIdToRemove) return;
    
    try {
      const playlistRef = doc(db, 'playlists', playlistId);
      await updateDoc(playlistRef, {
        songIds: arrayRemove(songIdToRemove)
      });
      fetchPlaylistAndSongs();
    } catch (error) {
      console.error("Error removing song from playlist: ", error);
      alert("Failed to remove song.");
    } finally {
      setActiveSongMenu(null);
    }
  };

  const handleGoToArtist = (profileId) => {
    if (!profileId) return;
    navigate(`/profile/${profileId}`);
    setActiveSongMenu(null);
  };

  const isOwner = activeProfile?.id && playlist?.ownerId === activeProfile.id;

  if (isLoading) {
    return <div className={`text-center p-10 text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-zinc-600 animate-pulse' : 'text-gray-400 animate-pulse'}`}>Loading playlist...</div>;
  }

  if (!playlist) {
    return <div className={`text-center p-10 text-xs font-bold uppercase tracking-widest ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>Playlist not found.</div>;
  }

  return (
    <div className={`p-4 relative pb-36 min-h-screen ${isDarkMode ? 'bg-[#090909] text-white' : 'bg-white text-slate-900'}`}>
      
      {/* Dynamic Three-dot Header Action Drawer Menu */}
      {isOwner && (
        <div ref={menuRef} className="absolute top-4 right-4 z-20">
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)} 
            className={`p-2 rounded-full transition-colors focus:outline-none ${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-200'}`}
          >
            <img src={dropdownSidewaysIcon} alt="Options" className={`w-6 h-6 ${isDarkMode ? 'invert opacity-70' : ''}`} />
          </button>
          
          <AnimatePresence>
            {isMenuOpen && (
              <motion.div 
                className={`absolute right-0 mt-2 w-52 border rounded-xl shadow-2xl py-1.5 z-30 ${
                  isDarkMode ? 'bg-zinc-950 border-zinc-800 text-zinc-300' : 'bg-white border-slate-100 text-slate-700'
                }`}
                initial={{ opacity: 0, scale: 0.95, y: -10 }} 
                animate={{ opacity: 1, scale: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                transition={{ duration: 0.15 }}
              >
                <button onClick={handleEditDetails} className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${isDarkMode ? 'hover:bg-zinc-900 hover:text-white' : 'hover:bg-gray-50 hover:text-slate-900'}`}>
                  Edit Details
                </button>
                <button onClick={handleEditArtwork} className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${isDarkMode ? 'hover:bg-zinc-900 hover:text-white' : 'hover:bg-gray-50 hover:text-slate-900'}`}>
                  Edit Artwork
                </button>
                
                {/* 🌟 NEW: Dynamic visibility toggle list switch option option */}
                <button 
                  onClick={handleTogglePrivacy} 
                  className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${
                    isDarkMode ? 'hover:bg-zinc-900 hover:text-brand-pink' : 'hover:bg-gray-50 hover:text-pink-600'
                  }`}
                >
                  Make {playlist.isPrivate ? 'Public' : 'Private'}
                </button>

                <div className={`border-t my-1 ${isDarkMode ? 'border-zinc-900' : 'border-slate-100'}`} />

                <button onClick={handleDeletePlaylist} className={`block w-full text-left px-4 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors ${isDarkMode ? 'hover:bg-zinc-900 hover:text-red-400' : 'hover:bg-gray-50 hover:text-red-600'}`}>
                  Delete Playlist
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Playlist Grid Cover Art Panel Component */}
      <div className="flex flex-col items-center mb-6 pt-8">
        <div className={`w-40 h-40 rounded-2xl overflow-hidden shadow-2xl mb-2 relative border ${isDarkMode ? 'border-zinc-800/80 shadow-black/40' : 'border-slate-100 shadow-slate-200'}`}>
          {isLoading ? (
            <div className={`w-full h-full ${isDarkMode ? 'bg-zinc-900 animate-pulse' : 'bg-gray-200 animate-pulse'}`}></div>
          ) : artworkUrls.length === 4 ? (
            <div className="grid grid-cols-2 grid-rows-2 h-full w-full">
              {artworkUrls.map((url, i) => (
                <img key={i} src={url} alt="Artwork" className="w-full h-full object-cover" />
              ))}
            </div>
          ) : (
            <img src={artworkUrls[0]} alt="Playlist" className="w-full h-full object-cover" />
          )}
        </div>
      </div>

      <h1 className="text-2xl md:text-3xl font-black italic uppercase tracking-tighter text-center sm:text-left">{playlist.name}</h1>
      <p className={`text-xs font-bold uppercase tracking-widest mb-6 text-center sm:text-left ${isDarkMode ? 'text-zinc-500' : 'text-gray-400'}`}>{songs.length} tracks</p>

      {playlist.description && (
        <div className="mb-6 max-w-2xl">
          <p className={`text-xs font-semibold leading-relaxed whitespace-pre-wrap ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>
            {(() => {
              const PREVIEW_LENGTH = 120;
              const needsTruncation = playlist.description.length > PREVIEW_LENGTH;
              
              const descriptionText = (isDescriptionExpanded || !needsTruncation)
                ? playlist.description
                : `${playlist.description.substring(0, PREVIEW_LENGTH)}... `;

              return (
                <>
                  {descriptionText}
                  {!isDescriptionExpanded && needsTruncation && (
                    <button 
                      onClick={() => setIsDescriptionExpanded(true)} 
                      className={`font-black uppercase tracking-wider text-[10px] ml-1 transition-colors ${isDarkMode ? 'text-zinc-200 hover:text-white' : 'text-slate-900 hover:underline'}`}
                    >
                      read more
                    </button>
                  )}
                  {isDescriptionExpanded && needsTruncation && (
                    <button 
                      onClick={() => setIsDescriptionExpanded(false)} 
                      className={`font-black uppercase tracking-wider text-[10px] ml-1 transition-colors ${isDarkMode ? 'text-zinc-200 hover:text-white' : 'text-slate-900 hover:underline'}`}
                    >
                      see less
                    </button>
                  )}
                </>
              );
            })()}
          </p>
        </div>
      )}
    
      {/* Owner Metadata Row Panel & Modded Add Tracks Button */}
      <div className="flex items-center justify-between my-4 pb-4 border-b border-zinc-900/40">
        {ownerProfile && (
          <div className="flex items-center space-x-2.5">
            <ProfileLink
              profileId={ownerProfile.id}
              photoURL={ownerProfile.photoURL}
              size="w-7 h-7"
              isPremium={ownerProfile.premiumSubscription?.status === 'active'}
            />
            <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-zinc-300' : 'text-slate-700'}`}>
              By {ownerProfile.displayName || 'Unknown User'}
            </span>
          </div>
        )}

        {isOwner && (
          // 🌟 UPGRADED: The Add Song Pillar Button has been given the Atomic high-fidelity execution
          <button
            onClick={() => setIsAddSongModalOpen(true)}
            className={`rounded-xl p-3 transition-all border shadow-md active:scale-95 focus:outline-none ${
              isDarkMode 
                ? "bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800 shadow-black/40" 
                : "bg-gray-100 border-slate-200 text-slate-700 hover:bg-gray-200 shadow-slate-100"
            }`}
            title="Add song to playlist"
          >
            <img 
              src={addToPlaylistIcon} 
              alt="Add to Playlist" 
              className={`w-5 h-5 ${isDarkMode ? 'invert opacity-80' : ''}`} 
            />
          </button>
        )}
      </div>

      <ul className="space-y-1">
        {isOwner ? (
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={songs.map(s => s.id)} strategy={verticalListSortingStrategy}>
              {songs.map((song, index) => (
                <SortableSongRow
                  key={song.id}
                  song={song}
                  index={index}
                  isPlaying={currentSong && currentSong.id === song.id}
                  isOwner={isOwner}
                  onPlay={() => handlePlaySong(index)}
                  activeSongMenu={activeSongMenu}
                  setActiveSongMenu={setActiveSongMenu}
                  menuPosition={menuPosition}
                  setMenuPosition={setMenuPosition}
                  songMenuRef={songMenuRef}
                  handleGoToArtist={handleGoToArtist}
                  handleRemoveSongFromPlaylist={handleRemoveSongFromPlaylist}
                  isDarkMode={isDarkMode} // 🌟 Forward state forward
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          songs.map((song, index) => (
            <li 
              key={song.id} 
              // 🌟 UPGRADED: Light/Dark mapping parameter for the non-owner row elements
              className={`flex items-center justify-between p-3 rounded-xl cursor-pointer transition-colors ${
                isDarkMode 
                  ? "hover:bg-zinc-900 text-white" 
                  : "hover:bg-gray-100 text-slate-900"
              }`}
              onClick={() => handlePlaySong(index)}
            >
              <div className="flex items-center flex-grow min-w-0 pr-2">
                <img src={song.albumArtworkURL} alt={song.title} className="w-14 h-14 rounded-xl object-cover mr-4 shadow-md flex-shrink-0" />
                <div className="flex-grow min-w-0">
                  <p className={`font-bold text-base tracking-tight truncate ${currentSong?.id === song.id ? 'text-brand-pink font-black italic' : ''}`}>
                    {song.title}
                  </p>
                  <p className={`text-xs font-medium truncate ${isDarkMode ? 'text-zinc-500' : 'text-gray-500'}`}>
                    {song.artistName}
                  </p>
                </div>
              </div>
            </li>
          ))
        )}
      </ul>

      <EditPlaylistModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        playlist={playlist}
        isDarkMode={isDarkMode}
        setPlaylist={setPlaylist}
      />

      <AddSongToPlaylistModal
        isOpen={isAddSongModalOpen}
        onClose={() => setIsAddSongModalOpen(false)}
        playlist={playlist}
        onSongAdded={fetchPlaylistAndSongs}
        isDarkMode={isDarkMode} // 🌟 Forward parameter state forward
      />

    </div>
  );
};

export default PlaylistDetailPage;